import { PartySlugSchema, isReservedPartySlug } from '@mixwire/contracts';

import type { ChannelId, PartySlug } from '@mixwire/contracts';

/** The all-parties lobby, and the fallback for anything that is not a party. */
export const LOBBY_PATH = '/';

/**
 * A party sits at the top level of the URL space so the link a DJ reads out is
 * `mixwire.example/summer-social` — see `RESERVED_PARTY_SLUGS` for the words
 * that costs us.
 */
export const partyPath = (slug: PartySlug): string => `/${slug}`;

export const djPath = (slug: PartySlug, channelId: ChannelId): string =>
  `/${slug}/dj/${String(channelId)}`;

export const suggestPath = (slug: PartySlug): string => `/${slug}/suggest`;

/** The passcode prompt, opened on arrival. */
export const suggestEntryPath = (slug: PartySlug): string => `/${slug}/add`;

/** The passcode rides in the query only until the suggester device consumes it. */
export const PASSKEY_QUERY_PARAM = 'code';

export const suggestInvitePath = (slug: PartySlug, passkey: string): string =>
  `${suggestPath(slug)}?${PASSKEY_QUERY_PARAM}=${encodeURIComponent(passkey)}`;

export const wrappedPath = (slug: PartySlug): string => `/${slug}/wrapped`;

export const exportPath = (slug: PartySlug): string => `/${slug}/export`;

/** Which party the lobby should open on, for someone sent there to join one they were already looking at. */
export const JOIN_QUERY_PARAM = 'join';

export const lobbyJoinPath = (slug: PartySlug): string =>
  `${LOBBY_PATH}?${JOIN_QUERY_PARAM}=${encodeURIComponent(slug)}`;

export const absoluteUrl = (path: string): string => new URL(path, location.origin).href;

/** `mixwire.example/summer-social` — the link as it should be read out or typed. */
export const displayLink = (path: string): string => `${location.host}${path}`;

/** Which party a path is about, whether it is the public view, a console or the suggester view. */
export const partySlugInPath = (path: string): PartySlug | undefined => {
  const first = path.split('/').find((segment) => segment !== '');
  if (first === undefined || isReservedPartySlug(first)) {
    return undefined;
  }
  const parsed = PartySlugSchema.safeParse(first);
  return parsed.success ? parsed.data : undefined;
};
